// /app/frontend/src/pages/admin/AdminAnalyticsV2Page.jsx
/**
 * Admin Analytics V2 Page
 * 
 * Enhanced analytics dashboard with:
 * - DAU/WAU/MAU
 * - Region breakdown
 * - Feature usage
 * - Conversion funnels
 * - Retention metrics
 */

import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/client";
import { AuthContext } from "../../App";
import { 
  ArrowLeft, 
  Globe, 
  TrendingUp, 
  Users, 
  MapPin,
  Activity,
  BarChart3,
  Repeat,
  ChevronDown
} from "lucide-react";
import { createInternalAxiosConfig } from "../../utils/internalApi";


function StatCard({ icon: Icon, label, value, subValue, trend, color = "purple" }) {
  const colorMap = {
    purple: "bg-purple-500/20 text-purple-400",
    green: "bg-green-500/20 text-green-400",
    blue: "bg-blue-500/20 text-blue-400",
    amber: "bg-amber-500/20 text-amber-400",
    pink: "bg-pink-500/20 text-pink-400",
  };

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
      <div className="flex items-start justify-between">
        <div className={`p-2 rounded-lg ${colorMap[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        {trend && (
          <span className={`text-xs ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold">{value?.toLocaleString() ?? '-'}</p>
        <p className="text-xs text-slate-400 mt-0.5">{label}</p>
        {subValue && (
          <p className="text-xs text-slate-500 mt-1">{subValue}</p>
        )}
      </div>
    </div>
  );
}

function FunnelBar({ label, count, rate, maxCount }) {
  const width = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-slate-300">{label}</span>
        <span className="text-slate-400">{count.toLocaleString()} ({rate}%)</span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export default function AdminAnalyticsV2Page() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [range, setRange] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [regions, setRegions] = useState(null);
  const [funnels, setFunnels] = useState(null);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      try {
        const config = createInternalAxiosConfig(token, 'admin');
        
        const [overviewRes, regionsRes, funnelsRes, retentionRes] = await Promise.all([
          apiClient.get(`/admin/analytics/v2/overview?range=${range}`, config),
          apiClient.get(`/admin/analytics/v2/regions?range=${range}`, config),
          apiClient.get(`/admin/analytics/v2/funnels?range=${range}`, config),
          apiClient.get(`/admin/analytics/v2/retention`, config),
        ]);
        
        setOverview(overviewRes.data);
        setRegions(regionsRes.data);
        setFunnels(funnelsRes.data);
        setRetention(retentionRes.data);
      } catch (err) {
        console.error("[AdminAnalyticsV2] Failed to load:", err);
        setError(err.response?.data?.detail || "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, [token, range]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-4"></div>
          <div className="text-sm text-slate-400">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 mb-4">⚠️ {error}</div>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition"
          >
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  const users = overview?.users || {};
  const activeUsers = overview?.active_users || {};
  const conversion = overview?.conversion || {};
  const featureUsage = overview?.feature_usage || [];
  const topRegions = regions?.top_regions_by_users || [];
  const funnelData = funnels?.signup_to_premium_funnel || {};
  const verifFunnel = funnels?.verification_funnel || {};
  const featureConversion = funnels?.feature_conversion || {};
  const retentionData = retention?.retention || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Admin Banner */}
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-purple-400 text-sm font-medium">🔒 Admin Only</span>
            <span className="text-slate-500 text-xs">|</span>
            <span className="text-slate-400 text-xs">Data refreshes on page load</span>
          </div>
          <span className="text-xs text-slate-500">
            Last updated: {new Date().toLocaleTimeString()}
          </span>
        </div>

        {/* Header */}
        <header className="border-b border-slate-800 pb-4">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Admin</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                📊 Analytics Dashboard
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                Regions, features, funnels, and retention
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="range-selector"
              >
                <option value="1d">Last 24h</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-800 pb-2">
          {["overview", "regions", "funnels", "retention"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === tab 
                  ? "bg-purple-500/20 text-purple-300" 
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                icon={Users} 
                label="Total Users" 
                value={users.total} 
                subValue={`${users.premium_rate}% premium`}
                color="purple"
              />
              <StatCard 
                icon={Activity} 
                label="DAU" 
                value={activeUsers.dau}
                subValue={`${activeUsers.dau_wau_ratio}% of WAU`}
                color="green"
              />
              <StatCard 
                icon={TrendingUp} 
                label="WAU" 
                value={activeUsers.wau}
                color="blue"
              />
              <StatCard 
                icon={BarChart3} 
                label="MAU" 
                value={activeUsers.mau}
                color="pink"
              />
            </div>

            {/* Conversion Overview */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-5">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-purple-400" />
                  Conversion (Last {range})
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">New Users</span>
                    <span>{conversion.new_users?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Converted to Premium</span>
                    <span className="text-green-400">{conversion.converted}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Conversion Rate</span>
                    <span className="text-purple-400 font-medium">{conversion.conversion_rate}%</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-5">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-amber-400" />
                  Feature Usage
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {featureUsage.slice(0, 6).map((f, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-400 capitalize">{f.feature}</span>
                      <span>{f.unique_users} users</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* User Breakdown */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-5">
              <h3 className="font-semibold mb-4">User Breakdown</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-purple-400">{users.total}</p>
                  <p className="text-xs text-slate-400">Total</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">{users.verified}</p>
                  <p className="text-xs text-slate-400">Verified ({users.verification_rate}%)</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-400">{users.premium}</p>
                  <p className="text-xs text-slate-400">Premium ({users.premium_rate}%)</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Regions Tab */}
        {activeTab === "regions" && (
          <div className="space-y-6">
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-400" />
                Top Regions by Users
              </h3>
              {topRegions.length === 0 ? (
                <p className="text-slate-500 text-sm">No region data available yet.</p>
              ) : (
                <div className="space-y-3">
                  {topRegions.slice(0, 10).map((r, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-slate-500 text-sm w-6">{i + 1}.</span>
                        <span>{r.region}</span>
                      </div>
                      <span className="text-slate-400">{r.user_count} users</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4 text-green-400" />
                Region Activity (Last {range})
              </h3>
              {regions?.top_regions_by_activity?.length === 0 ? (
                <p className="text-slate-500 text-sm">No activity data by region yet.</p>
              ) : (
                <div className="space-y-3">
                  {(regions?.top_regions_by_activity || []).slice(0, 10).map((r, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-slate-500 text-sm w-6">{i + 1}.</span>
                        <span>{r.region}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-slate-400">{r.unique_users} users</span>
                        <span className="text-slate-600 mx-2">·</span>
                        <span className="text-slate-500">{r.events} events</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Funnels Tab */}
        {activeTab === "funnels" && (
          <div className="space-y-6">
            {/* Signup to Premium Funnel */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-400" />
                Signup → Premium Funnel
              </h3>
              <div className="space-y-4">
                <FunnelBar 
                  label="Signups" 
                  count={funnelData.signups || 0} 
                  rate={100}
                  maxCount={funnelData.signups || 1}
                />
                <FunnelBar 
                  label="Profile Completed" 
                  count={funnelData.profiles_completed || 0} 
                  rate={funnelData.profile_rate || 0}
                  maxCount={funnelData.signups || 1}
                />
                <FunnelBar 
                  label="Engaged (Used Feature)" 
                  count={funnelData.engaged_users || 0} 
                  rate={funnelData.engagement_rate || 0}
                  maxCount={funnelData.signups || 1}
                />
                <FunnelBar 
                  label="Converted to Premium" 
                  count={funnelData.converted || 0} 
                  rate={funnelData.conversion_rate || 0}
                  maxCount={funnelData.signups || 1}
                />
              </div>
            </div>

            {/* Verification Funnel */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Activity className="w-4 h-4 text-amber-400" />
                Verification Funnel
              </h3>
              <div className="space-y-4">
                <FunnelBar 
                  label="Started Verification" 
                  count={verifFunnel.started || 0} 
                  rate={100}
                  maxCount={verifFunnel.started || 1}
                />
                <FunnelBar 
                  label="Completed" 
                  count={verifFunnel.completed || 0} 
                  rate={verifFunnel.completion_rate || 0}
                  maxCount={verifFunnel.started || 1}
                />
              </div>
            </div>

            {/* Feature → Conversion */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-pink-400" />
                Feature → Paid Conversion
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-sm text-slate-400 mb-2">Lounge Users</h4>
                  <p className="text-xl font-bold">{featureConversion?.lounge?.users || 0}</p>
                  <p className="text-sm text-green-400">
                    {featureConversion?.lounge?.converted || 0} converted ({featureConversion?.lounge?.rate || 0}%)
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-sm text-slate-400 mb-2">Speed Dating Users</h4>
                  <p className="text-xl font-bold">{featureConversion?.speeddating?.users || 0}</p>
                  <p className="text-sm text-green-400">
                    {featureConversion?.speeddating?.converted || 0} converted ({featureConversion?.speeddating?.rate || 0}%)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Retention Tab */}
        {activeTab === "retention" && (
          <div className="space-y-6">
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Repeat className="w-4 h-4 text-green-400" />
                Retention Rates
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-purple-400">{retentionData?.d1?.rate || 0}%</p>
                  <p className="text-sm text-slate-400 mt-1">D1 Retention</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {retentionData?.d1?.retained || 0} / {retentionData?.d1?.cohort_size || 0}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{retentionData?.d7?.rate || 0}%</p>
                  <p className="text-sm text-slate-400 mt-1">D7 Retention</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {retentionData?.d7?.retained || 0} / {retentionData?.d7?.cohort_size || 0}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-amber-400">{retentionData?.d30?.rate || 0}%</p>
                  <p className="text-sm text-slate-400 mt-1">D30 Retention</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {retentionData?.d30?.retained || 0} / {retentionData?.d30?.cohort_size || 0}
                  </p>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-4 text-center">
                Retention = users who returned X days after signup
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
